import { Loading } from "@lyfegen-platform/react-common";
import { sortBySelectors } from "@lyfegen-platform/utils";
import { Box, Switch, Typography } from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import LyfeTooltip from "../../../../components/LyfeTooltip";
import { useDataMutation } from "../../../../hooks/useDataModule";
import { SelectableValue } from "../../../../models/common/selectable-value";
import { Role } from "../../../../models/identity/role";
import { userPersonalInfoState } from "../../../../store/user";
import startUpper from "../../../../utils/start-upper";
import { ADD_MODULE, GET_GENERAL_CONFIGURATION_WITH_DOMAINS, REMOVE_MODULE, UPDATE_USER_ROLE } from "../queries";
import { GetGeneralConfigurationWithDomainsQuery, GetUserDomainsTreeQuery } from "../types/queries.generated";
import useOrganizationStyles from "../use-organization-styles";
import FlexTypographyComponent from "../../../../components/FlexTypographyComponent";
import { DomainMultiSelect } from "./DomainMultiSelect";
import { sortSameLevelDomains } from "../../../simulator/details/utils/sort-domains";
import { useLocation } from "react-router-dom";
import { useLazyModelsQuery } from "apps/platform-client/client/src/hooks/useModelsModule";
export interface OperativeDataProps {
	orgId: string;
	domainOptionsTree: GetUserDomainsTreeQuery["GetUserDomainsTree"];
	domainOptions: Array<SelectableValue>;
	selectedUsers: Array<SelectableValue>;
	selectedTags: Array<SelectableValue>;
	selectedDomains: Array<SelectableValue>;
}

const setNextPageToast = (showValidationError: boolean, message: string) => {
	showValidationError && sessionStorage.setItem("redirectToastMessage", message);
};

const OperativeDataAction: React.FC<OperativeDataProps> = ({
	orgId,
	domainOptions,
	domainOptionsTree,
	selectedUsers,
	selectedTags,
	selectedDomains,
}) => {
	const { operativeDataAccessContainer, generalConfigurationTable, infoIcon, userMailColumn } = useOrganizationStyles();
	const lUser = useRecoilValue(userPersonalInfoState);
	const location = useLocation();
	const [usersWithNonSelectedDomains, setUsersWithNonSelectedDomains] = useState<string[]>([]);
	const [operativeData, setOperativeData] = useState<GetGeneralConfigurationWithDomainsQuery["GetGeneralConfigurationWithDomains"]>([]);
	const { t } = useTranslation();
	const [refetch, { data, loading }] = useLazyModelsQuery<GetGeneralConfigurationWithDomainsQuery>(
		GET_GENERAL_CONFIGURATION_WITH_DOMAINS,
		{
			variables: {
				input: {
					id: orgId,
					users: selectedUsers.map((user) => user.value),
					tags: selectedTags.map((tag) => tag.value),
					domains: selectedDomains.map((domain) => domain.value),
				},
			},
			fetchPolicy: "no-cache",
		}
	);

	const mutationsVariable = {
		onCompleted: () => {
			refetch();
		},
	};
	const [mutate] = useDataMutation(UPDATE_USER_ROLE, mutationsVariable);
	const [addDataModule] = useDataMutation(ADD_MODULE, mutationsVariable);
	const [removeDataModule] = useDataMutation(REMOVE_MODULE, mutationsVariable);
	const dataModule = { id: 5, name: "Data", code: "5" };
	const handleRoleUpdate = (user: any, e: React.ChangeEvent<HTMLInputElement>) => {
		if (!user) return;
		mutate({ variables: { id: user.userId, isAdmin: e.target.value !== "true" } });
	};
	useEffect(() => {
		refetch();
	}, [selectedUsers]);

	// Show alert when navigating away
	useEffect(() => {
		setNextPageToast(!!usersWithNonSelectedDomains.length, t("simulator-domain-user-assignation-reverted-message"));
	}, [location.pathname, usersWithNonSelectedDomains]);

	useEffect(() => {
		setOperativeData(
			(data?.GetGeneralConfigurationWithDomains || [])
				.filter(
					(configuration) =>
						!selectedDomains.length ||
						selectedDomains.some((selectedDomain) => configuration.domainIds.includes(selectedDomain.value))
				)
				.slice()
				.sort(sortBySelectors((a: GetGeneralConfigurationWithDomainsQuery["GetGeneralConfigurationWithDomains"][0]) => a.firstName))
		);
	}, [data]);

	const handleModuleUpdate = (user: any, e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.value === "true") {
			removeDataModule({ variables: { id: user.userId, moduleId: dataModule.id } });
			return;
		}
		addDataModule({ variables: { id: user.userId, input: dataModule } });
	};

	return (
		<Fragment>
			<Box hidden={loading} mt={1} className={operativeDataAccessContainer}>
				<table width="100%" className={generalConfigurationTable}>
					<thead>
						<tr>
							<th>{startUpper(t("username"))}</th>
							<th style={{ textAlign: "left" }}>{startUpper(t("pdf-email"))}</th>
							<th>
								{startUpper(t("administrator-rights"))}
								<LyfeTooltip title={t("administrator_rights_description")}>
									<InfoOutlined className={infoIcon} fontSize="small" />
								</LyfeTooltip>
							</th>
							<th>
								{startUpper(t("operative-data-access"))}
								<LyfeTooltip title={t("operative_data_access_description")}>
									<InfoOutlined className={infoIcon} fontSize="small" />
								</LyfeTooltip>
							</th>
							<th>{startUpper(t("organization-settings-tab-domain"))}</th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						{operativeData &&
							operativeData.map((ur, idx) => (
								<tr key={idx}>
									<td>
										<Typography variant="body2">
											{ur.lastName} {" " + ur.firstName}
										</Typography>
									</td>
									<td>
										<Typography className={userMailColumn}>
											<FlexTypographyComponent variant="body2" text={ur.email || ""} />
										</Typography>
									</td>
									<td>
										<LyfeTooltip title={ur.role === Role.User ? `${t("disabled")}` : `${t("enabled")}`}>
											<Switch
												onChange={(e) => {
													handleRoleUpdate(ur, e);
												}}
												disabled={ur.userId === lUser.id}
												checked={ur.role === Role.Administrator}
												value={ur.role === Role.Administrator}
											/>
										</LyfeTooltip>
									</td>
									<td>
										<LyfeTooltip title={ur.hasOperativeDataAccess ? `${t("enabled")}` : `${t("disabled")}`}>
											<Switch
												onChange={(e) => {
													handleModuleUpdate(ur, e);
												}}
												disabled={false}
												checked={ur.hasOperativeDataAccess!}
												value={ur.hasOperativeDataAccess}
											/>
										</LyfeTooltip>
									</td>
									<td>
										<DomainMultiSelect
											domainOptionsTree={sortSameLevelDomains(domainOptionsTree)}
											values={ur.domainIds}
											userId={ur.userId!}
											email={ur.email}
											domains={domainOptions}
											setUsersWithNonSelectedDomains={setUsersWithNonSelectedDomains}
										/>
									</td>
									<td></td>
								</tr>
							))}
					</tbody>
				</table>
			</Box>
			{loading && (
				<Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
					<Loading />
				</Box>
			)}
		</Fragment>
	);
};

export default OperativeDataAction;
